import { ReactElement, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { makeCrossSectionLabel } from '@editorUtils'
import { Alert, LinearProgress, Stack, Typography, useTheme } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import type { GridCellParams, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useElementLabel } from '@editorHooks'
import {
  utilizationCellClassName,
  utilizationGridSx,
} from '../../MultiEditCheckSettings/components/CheckSettingsElementTable'
import useLiveCheckRecompute from '../../MultiEditCheckSettings/hooks/useLiveCheckRecompute'
import { CheckSettingsFormValues } from '../../MultiEditCheckSettings/types'
import useToggleExcludedSupports from '../hooks/useToggleExcludedSupports'
import {
  SharedSupportCompressionSettings,
  SupportCellState,
  getMaxSupportCount,
  getSupportCellStates,
  getSupportConfigs,
  hasIncludedSupport,
  isSupportIndexExcluded,
  mapSupportCompressionChecks,
  mergeSupportCompressionSettings,
  shouldPreviewSupportCompression,
  supportColumnLabel,
} from '../utils'
import SupportColumnHeader from './SupportColumnHeader'
import SupportMatrixCell from './SupportMatrixCell'

interface SupportCompressionMatrixRow {
  elementGuid: string
  label: string
  crossSectionLabel?: string
  supportConfigs: SupportConfig[]
  supportChecks: (SupportCompressionStructuralCheck | undefined)[]
  supportStates: SupportCellState[]
}

/**
 * One row per selected member, one column per support (A1, A2, …), each cell
 * previewing the support compression utilization of that support. Recomputed
 * live while the staged shared field changes, before anything is saved. A cell
 * can remove its support from the edit — it then shows a „+" to add it back and
 * is neither previewed nor written on save.
 */
const SupportCompressionMatrix = (): ReactElement => {
  const theme = useTheme()
  const getLabel = useElementLabel()

  const { toggleSupport, toggleSupportColumn, toggleAllSupports } = useToggleExcludedSupports()

  const elements = useWatch<CheckSettingsFormValues<SharedSupportCompressionSettings>>({
    name: 'elements',
  }) as CheckSettingsFormValues['elements']
  const sharedSettings = useWatch<CheckSettingsFormValues<SharedSupportCompressionSettings>>({
    name: 'sharedSettings',
  }) as SharedSupportCompressionSettings | undefined

  const { isUpdatingChecks, unresolvedElementCount } =
    useLiveCheckRecompute<SharedSupportCompressionSettings>({
      sectionKey: 'supportCompression',
      mergeSettings: mergeSupportCompressionSettings,
      isFormValid: shouldPreviewSupportCompression,
    })

  const supportCount = useMemo(
    () => getMaxSupportCount(elements, sharedSettings?.irrelevantSupports),
    [elements, sharedSettings?.irrelevantSupports],
  )

  const rows = useMemo<SupportCompressionMatrixRow[]>(
    () =>
      elements.map(element => {
        const supportConfigs = getSupportConfigs(element.checkSettings) ?? []

        return {
          elementGuid: element.guid,
          label: getLabel(element.guid),
          crossSectionLabel: element.crossSection
            ? makeCrossSectionLabel(element.crossSection)
            : undefined,
          supportConfigs,
          supportChecks: mapSupportCompressionChecks(element.checks, supportConfigs),
          supportStates: getSupportCellStates(element, sharedSettings, supportCount),
        }
      }),
    [elements, sharedSettings, supportCount, getLabel],
  )

  const allSupportsExcluded = useMemo(
    () => !hasIncludedSupport(elements, sharedSettings),
    [elements, sharedSettings],
  )

  const excludedSupportColumns = useMemo(
    () =>
      Array.from({ length: supportCount }, (_, supportIndex) =>
        isSupportIndexExcluded(elements, sharedSettings, supportIndex),
      ),
    [elements, sharedSettings, supportCount],
  )

  const columns = useMemo<GridColDef<SupportCompressionMatrixRow>[]>(
    () => [
      {
        field: 'label',
        headerName: 'Element',
        flex: 0,
        minWidth: 110,
        renderHeader: () => (
          <SupportColumnHeader
            label="Element"
            isExcluded={allSupportsExcluded}
            onToggle={toggleAllSupports}
            removeTooltip="Alle Auflager entfernen"
            addTooltip="Alle Auflager wieder berücksichtigen"
            justifyContent="flex-start"
            dataCy="support-compression-matrix-header-all"
          />
        ),
        renderCell: (params: GridRenderCellParams<SupportCompressionMatrixRow>) => (
          <Stack direction="column" justifyContent="center">
            <Typography variant="body2">{params.row.label}</Typography>
            {params.row.crossSectionLabel && (
              <Typography variant="caption" color="text.secondary">
                {params.row.crossSectionLabel}
              </Typography>
            )}
          </Stack>
        ),
        display: 'flex',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
      },
      ...Array.from(
        { length: supportCount },
        (_, supportIndex): GridColDef<SupportCompressionMatrixRow> => ({
          field: `support-${supportIndex}`,
          headerName: supportColumnLabel(supportIndex),
          width: 76,
          minWidth: 76,
          align: 'center',
          headerAlign: 'center',
          renderHeader: () => (
            <SupportColumnHeader
              label={supportColumnLabel(supportIndex)}
              isExcluded={excludedSupportColumns[supportIndex]}
              onToggle={() => toggleSupportColumn(supportIndex)}
              removeTooltip={`${supportColumnLabel(supportIndex)} für alle Elemente entfernen`}
              addTooltip={`${supportColumnLabel(
                supportIndex,
              )} für alle Elemente wieder berücksichtigen`}
              dataCy={`support-compression-matrix-header-${supportIndex}`}
            />
          ),
          renderCell: (params: GridRenderCellParams<SupportCompressionMatrixRow>) => (
            <SupportMatrixCell
              state={params.row.supportStates[supportIndex]}
              utilization={params.row.supportChecks[supportIndex]?.max_utilization}
              onToggle={() => toggleSupport(params.row.elementGuid, supportIndex)}
              dataCy={`support-compression-matrix-${params.row.elementGuid}-${supportIndex}`}
            />
          ),
          cellClassName: (params: GridCellParams<SupportCompressionMatrixRow>) =>
            params.row.supportStates[supportIndex] === 'editable'
              ? utilizationCellClassName(params.row.supportChecks[supportIndex]?.max_utilization)
              : '',
          display: 'flex',
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
        }),
      ),
    ],
    [
      supportCount,
      excludedSupportColumns,
      allSupportsExcluded,
      toggleSupport,
      toggleSupportColumn,
      toggleAllSupports,
    ],
  )

  return (
    <Stack direction="column" spacing={0.5} pt={1} data-cy="support-compression-matrix">
      {unresolvedElementCount > 0 && (
        <Alert severity="warning" data-cy="support-compression-matrix-unresolved-warning">
          {`Vorschau für ${unresolvedElementCount} Element(e) nicht verfügbar (Querschnitt nicht ermittelbar)`}
        </Alert>
      )}
      {isUpdatingChecks && <LinearProgress />}
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={row => row.elementGuid}
        getRowHeight={() => 'auto'}
        hideFooterPagination
        hideFooterSelectedRowCount
        hideFooter
        autosizeOnMount
        autosizeOptions={{ includeHeaders: false, expand: true }}
        disableColumnSorting
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        density="compact"
        sx={utilizationGridSx(theme)}
      />
    </Stack>
  )
}

export default SupportCompressionMatrix
